import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import {
  MagnifyingGlassIcon, ArrowUturnLeftIcon, ExclamationTriangleIcon,
  CheckCircleIcon, XCircleIcon,
} from '@heroicons/react/24/outline';
import styles from './ActivityPage.module.css';

const PROVIDER_LABELS = {
  cybersource: 'Card',
  whish:       'Whish',
};

// A refund moves real money and cannot be undone from here, so the
// confirmation asks for the amount to be typed rather than offering a button
// to click straight through.
function ConfirmRefund({ detail, onCancel, onDone }) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const amount = detail.payment.amount.toFixed(2);
  const ready = typed.trim() === amount && reason.trim().length >= 4 && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError('');
    try {
      const res = await adminAPI.refundPayment(detail.payment.id, { reason: reason.trim() });
      onDone(res);
    } catch (e) {
      setError(e.message || 'The refund could not be completed.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Refund ${amount}</h2>

        <div style={{ marginBottom: 16, lineHeight: 1.7 }}>
          <div><strong>{detail.member.name || 'Unknown member'}</strong> — {detail.member.mobile}</div>
          <div style={{ color: '#64748b' }}>
            {PROVIDER_LABELS[detail.payment.provider] || detail.payment.provider}
            {detail.payment.card
              ? ' · ' + (detail.payment.card.brand ? detail.payment.card.brand + ' ' : '')
                + '•••• ' + detail.payment.card.last4
              : ''}
            {detail.payment.paidAt
              ? ' · paid ' + format(new Date(detail.payment.paidAt), 'd MMM yyyy')
              : ''}
          </div>
          {detail.membership && (
            <div style={{ color: '#64748b' }}>
              Card {detail.membership.code} — will be withdrawn
            </div>
          )}
          {detail.payment.card && detail.payment.card.holder && (
            <div style={{ color: '#64748b' }}>
              Cardholder: <strong>{detail.payment.card.holder}</strong>
              {detail.payment.card.email ? ' · ' + detail.payment.card.email : ''}
            </div>
          )}
        </div>

        {detail.gatewayAutomatic && detail.payment.sameDay === false && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
            padding: 12, marginBottom: 16, fontSize: 14, lineHeight: 1.6,
            color: '#991b1b',
          }}>
            <strong>This was not paid today.</strong> BLOM support same-day card
            refunds only, so the gateway will probably refuse this one. If it
            does, nothing is changed — the membership stays active and the
            refund has to be arranged with the bank.
          </div>
        )}

        <div style={{
          background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8,
          padding: 12, marginBottom: 16, fontSize: 14, lineHeight: 1.6,
        }}>
          <strong>This will:</strong>
          <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
            <li>
              {detail.gatewayAutomatic
                ? 'Return the money through the card gateway'
                : 'NOT return the money automatically — Whish has no refund API yet'}
            </li>
            <li>Reverse the VIP points in Business Central</li>
            <li>Withdraw the membership, closing their QR code immediately</li>
          </ol>
          <div style={{ marginTop: 8 }}>
            Business Central takes a minute or two to move them off the VIP
            scheme. Until it does, the till may still read them as VIP.
          </div>
          {detail.note && (
            <div style={{ marginTop: 8, color: '#9a3412' }}>{detail.note}</div>
          )}
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Why is this being refunded?
        </label>
        <input
          className={styles.searchInput}
          style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8 }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Customer asked to cancel"
          autoFocus
        />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 4px' }}>
          Type <strong>{amount}</strong> to confirm
        </label>
        <input
          className={styles.searchInput}
          style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8 }}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={amount}
        />

        {error && (
          <div style={{ color: '#b91c1c', marginTop: 12, fontSize: 14 }}>{error}</div>
        )}

        <div className="modal-actions">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn-danger" onClick={submit} disabled={!ready}>
            {busy ? 'Refunding…' : 'Refund $' + amount}
          </button>
        </div>
      </div>
    </div>
  );
}

// What actually happened, step by step. A refund can half-succeed — the money
// goes back but BC refuses the points — and hiding that would leave somebody
// believing the job was finished when it was not.
function Result({ result, onClose }) {
  const step = (label, s) => {
    if (!s) return null;
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
        {s.ok
          ? <CheckCircleIcon style={{ width: 20, height: 20, color: '#16a34a', flexShrink: 0 }} />
          : <XCircleIcon style={{ width: 20, height: 20, color: s.manual ? '#d97706' : '#b91c1c', flexShrink: 0 }} />}
        <div>
          <div><strong>{label}</strong></div>
          {!s.ok && s.reason && (
            <div style={{ color: '#64748b', fontSize: 13 }}>{s.reason}</div>
          )}
          {s.ok && s.message && (
            <div style={{ color: '#64748b', fontSize: 13 }}>{s.message}</div>
          )}
        </div>
      </div>
    );
  };

  // Everything that was supposed to happen, happened. Worth saying outright:
  // three ticks in a row is a conclusion the reader should not have to draw
  // for themselves.
  const allOk = !!(result.steps.gateway && result.steps.gateway.ok
    && result.steps.points && result.steps.points.ok
    && result.steps.membership && result.steps.membership.ok);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>
          Refunded ${result.amount.toFixed(2)}
          {result.member ? ' — ' + result.member : ''}
        </h2>

        {allOk && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
            padding: 12, marginBottom: 16, display: 'flex', gap: 8,
            alignItems: 'flex-start',
          }}>
            <CheckCircleIcon style={{ width: 20, height: 20, color: '#16a34a', flexShrink: 0 }} />
            <div style={{ fontSize: 14, lineHeight: 1.6, color: '#166534' }}>
              <strong>Refund completed.</strong> The money is on its way back,
              the VIP points are reversed and the member&rsquo;s QR is closed.
              Nothing further to do.
            </div>
          </div>
        )}

        <div style={{ margin: '16px 0' }}>
          {step('Money returned', result.steps.gateway)}
          {step('Refund sent to Business Central', result.steps.points)}
          {step('Membership withdrawn — QR closed', result.steps.membership)}
        </div>

        {result.steps.points && result.steps.points.ok && (
          <div style={{
            background: '#f1f5f9', borderRadius: 8, padding: 12,
            fontSize: 13, lineHeight: 1.6, color: '#475569', marginBottom: 12,
          }}>
            Business Central applies the refund with a short delay — the member
            moves from VIP to SILVER a minute or two from now. Until then the
            till may still read them as VIP. We re-check automatically, and the
            3-hourly sync catches anything that slips through.
          </div>
        )}

        {result.outstanding && result.outstanding.length > 0 && (
          <div style={{
            background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8,
            padding: 12, fontSize: 14, lineHeight: 1.6,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <ExclamationTriangleIcon style={{ width: 18, height: 18, color: '#d97706' }} />
              <strong>Still to do by hand</strong>
            </div>
            <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
              {result.outstanding.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

export default function RefundsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('open');   // 'open' = refundable, 'history'
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (search) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.refundablePayments(search ? { q: search } : {});
      setRows(res.payments || []);
    } catch (e) {
      setError(e.message || 'Could not load payments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const openRefund = async (id) => {
    setError('');
    try {
      const d = await adminAPI.refundPreview(id);
      if (!d.refundable) {
        setError('That payment cannot be refunded.');
        return;
      }
      setDetail(d);
    } catch (e) {
      setError(e.message || 'Could not load the payment.');
    }
  };

  const open = rows.filter((r) => !r.refunded);
  const history = rows.filter((r) => r.refunded);
  const shown = tab === 'history' ? history : open;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Refunds</h1>
          <p>
            Return the money, reverse the VIP points and close the member&rsquo;s QR
            — in one action.
          </p>
        </div>
      </div>

      <div className={styles.filters}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            ['open', 'To refund', open.length],
            ['history', 'History', history.length],
          ].map(([key, label, count]) => (
            <button
              key={key}
              className={`${styles.statusTab} ${tab === key ? styles.statusTabActive : ''}`}
              onClick={() => setTab(key)}
            >
              {label} ({count})
            </button>
          ))}
        </div>
        <form
          className={styles.searchBox}
          onSubmit={(e) => { e.preventDefault(); load(q); }}
        >
          <MagnifyingGlassIcon style={{ width: 16, height: 16, color: '#94a3b8' }} />
          <input
            className={styles.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, phone or gateway reference"
          />
        </form>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          borderRadius: 8, padding: 12, marginBottom: 16,
        }}>{error}</div>
      )}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{tab === 'history' ? 'Refunded' : 'Paid'}</th>
              <th>Member</th>
              <th>Phone</th>
              <th>Method</th>
              <th>Paid with</th>
              <th>VIP card</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              {tab === 'history' && <th>Result</th>}
              <th>{tab === 'history' ? 'Reason' : ''}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td {...{ colSpan: tab === 'history' ? 9 : 8 }} className="empty-state">Loading…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td {...{ colSpan: tab === 'history' ? 9 : 8 }} className="empty-state">
                {tab === 'history'
                  ? 'Nothing has been refunded yet.'
                  : 'No refundable payments.'}
              </td></tr>
            ) : shown.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {tab === 'history'
                    ? (r.refundedAt
                        ? format(new Date(r.refundedAt), 'd MMM yyyy, HH:mm')
                        : '—')
                    : (r.paidAt ? format(new Date(r.paidAt), 'd MMM yyyy') : '—')}
                </td>
                <td>{r.member || '—'}</td>
                <td>{r.mobile}</td>
                <td>
                  {PROVIDER_LABELS[r.provider] || r.provider}
                  {!r.gatewayAutomatic && (
                    <span className="badge badge-warning" style={{ marginLeft: 6 }}>manual</span>
                  )}
                </td>
                <td>
                  {r.card ? (
                    <div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {r.card.brand ? r.card.brand + ' ' : ''}&bull;&bull;&bull;&bull; {r.card.last4}
                      </div>
                      {r.card.holder && (
                        <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                          {r.card.holder}
                        </div>
                      )}
                    </div>
                  ) : <span style={{ color: '#94a3b8' }}>—</span>}
                </td>
                <td>{r.memberCode || '—'}</td>
                <td style={{ textAlign: 'right' }}>${Number(r.amount).toFixed(2)}</td>
                {tab === 'history' && (
                  <td>
                    {r.refundClean ? (
                      <span className="badge badge-success" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        <CheckCircleIcon style={{ width: 13, height: 13 }} />
                        Refunded
                      </span>
                    ) : (
                      <span className="badge badge-warning" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        <ExclamationTriangleIcon style={{ width: 13, height: 13 }} />
                        {r.refundMoneyManual ? 'Send money' : 'Points pending'}
                      </span>
                    )}
                  </td>
                )}
                <td style={{ textAlign: 'right' }}>
                  {r.refunded ? (
                    <span style={{ color: '#64748b', fontSize: 13 }}>
                      {/* "Refunded by Super Admin: wrong card" -> who and why. */}
                      {String(r.refundNote || '')
                        .replace(/^Refunded by /, '')
                        .replace(/\s*\[(points not reversed|money sent by hand)\]/g, '')
                        || 'Refunded'}
                    </span>
                  ) : (
                    <button className="btn-ghost" onClick={() => openRefund(r.id)}>
                      <ArrowUturnLeftIcon style={{ width: 15, height: 15 }} /> Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <ConfirmRefund
          detail={detail}
          onCancel={() => setDetail(null)}
          onDone={(res) => { setDetail(null); setResult(res); setTab('history'); load(q); }}
        />
      )}

      {result && <Result result={result} onClose={() => setResult(null)} />}
    </div>
  );
}
